// ============================================================================
// SCHEDULING VIEW — Stage 4: Appointment Booking (scheduler role only)
//
// Role: scheduler only. Sidebar: Scheduling only.
//
// BookAppointmentModal uses a Calendly-style calendar + time-slot picker:
//   Left pane  — month calendar grid, navigate prev/next month
//   Right pane — available time slots (shown after date selected)
//   Below      — Consultant dropdown + Note (non-PHI)
//   Footer     — Cancel | Book Appointment
//
// Three tabs: Ready to Schedule / Scheduled / Referral Outcomes
// Post-consult completion (SCHEDULED → CLOSED_LOOP) belongs to SpecialistWorklistView.
//
// Firestore writes (scheduler only, tenant-scoped):
//   Booking: workflow/state (SCHEDULED + scheduling.*) + workflowEvents
//
// Zero-PHI: no patient name, DOB, MRN rendered.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Calendar, Loader2, AlertCircle, RefreshCw,
  Clock, User, X, XCircle, Undo2, Bell,
  ChevronLeft, ChevronRight, CheckCircle2,
} from 'lucide-react';
import {
  WORKFLOW_STATUS, WORKFLOW_PRIORITY, listWorkqueueItems,
  CANCEL_REASON_LABELS, RETURN_REASON_LABELS,
} from '../core/index.js';
import { getReferral } from '../core/store.referrals.js';
import { findReferralInBuckets, classifyDeepLinkFailure } from '../core/deepLinkReferral.js';
import { setScheduled } from '../core/store.scheduling.js';
import { useAuth } from '../auth/AuthContext.jsx';
import WorkItemDrawer from './WorkItemDrawer.jsx';
import BookAppointmentModal, {
  CalendarPicker,
  TIME_SLOTS, DAY_HEADERS,
  fmtLabel, fmtVisitType, fmtVisitPurpose,
  to24h, from24h,
} from './BookAppointmentModal.jsx';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';
import ModifyScheduledModal from './ModifyScheduledModal.jsx';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, query, where, getDocs,
} from 'firebase/firestore';

// ── Helpers (SchedulingView-local) ────────────────────────────────────────────

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 14 ? `…${id.slice(-10)}` : id;
}

// ── Sort functions (one per tab) ──────────────────────────────────────────────

/**
 * Ready to Schedule:
 *   Primary  — priority descending: URGENT → HIGH → NORMAL → null/unknown (last)
 *   Secondary — createdAt ascending: oldest first within the same priority band
 */
const PRIORITY_RANK = {
  [WORKFLOW_PRIORITY.URGENT]: 0,
  [WORKFLOW_PRIORITY.HIGH]:   1,
  [WORKFLOW_PRIORITY.NORMAL]: 2,
};
function sortReady(arr) {
  return [...arr].sort((a, b) => {
    const pa = PRIORITY_RANK[a.workflow?.priority] ?? 3; // null/unknown → last
    const pb = PRIORITY_RANK[b.workflow?.priority] ?? 3;
    if (pa !== pb) return pa - pb;
    // Within same priority: oldest triage completion first
    const tA = a.workflow?.triage?.triageCompletedAt?.seconds ?? a.workflow?.triageCompletedAt?.seconds ?? 0;
    const tB = b.workflow?.triage?.triageCompletedAt?.seconds ?? b.workflow?.triageCompletedAt?.seconds ?? 0;
    return tA - tB;
  });
}

/**
 * Scheduled — Upcoming:
 *   Primary — appointmentDate ascending: soonest first
 *   Missing appointmentDate → bottom
 */
function sortScheduled(arr) {
  return [...arr].sort((a, b) => {
    const dA = a.workflow?.scheduling?.appointmentDate ?? null;
    const dB = b.workflow?.scheduling?.appointmentDate ?? null;
    if (!dA && !dB) return 0;
    if (!dA) return 1;  // missing → bottom
    if (!dB) return -1;
    return dA < dB ? -1 : dA > dB ? 1 : 0; // YYYY-MM-DD string comparison is safe
  });
}

/**
 * Seen:
 *   Primary — scheduling.seenAt descending: most recently seen first
 *   Missing seenAt → bottom
 */
function sortSeen(arr) {
  return [...arr].sort((a, b) => {
    const sA = a.workflow?.scheduling?.seenAt?.seconds ?? null;
    const sB = b.workflow?.scheduling?.seenAt?.seconds ?? null;
    if (sA === null && sB === null) return 0;
    if (sA === null) return 1;  // missing → bottom
    if (sB === null) return -1;
    return sB - sA; // descending: largest (most recent) first
  });
}

/**
 * Referral Outcomes — most recently resolved first.
 * closedAt desc → seenAt desc → updatedAt desc
 */
function tsSec(ts) {
  if (!ts) return null;
  if (typeof ts.seconds === 'number') return ts.seconds;
  if (typeof ts.toMillis === 'function') return ts.toMillis() / 1000;
  return null;
}

function sortClosed(arr) {
  return [...arr].sort((a, b) => {
    const ca = tsSec(a.workflow?.closedLoop?.closedAt) ?? tsSec(a.workflow?.cancelledAt) ?? tsSec(a.workflow?.noShow?.noShowAt);
    const cb = tsSec(b.workflow?.closedLoop?.closedAt) ?? tsSec(b.workflow?.cancelledAt) ?? tsSec(b.workflow?.noShow?.noShowAt);
    if (ca !== cb) { if (ca === null) return 1; if (cb === null) return -1; return cb - ca; }
    const sa = tsSec(a.workflow?.scheduling?.seenAt);
    const sb = tsSec(b.workflow?.scheduling?.seenAt);
    if (sa !== sb) { if (sa === null) return 1; if (sb === null) return -1; return sb - sa; }
    return (tsSec(b.workflow?.updatedAt) ?? 0) - (tsSec(a.workflow?.updatedAt) ?? 0);
  });
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function SectionHeader({ label, count, tw = 'text-slate-400' }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <p className={`text-xs font-semibold uppercase tracking-wider ${tw}`}>{label}</p>
      <span className={`text-xs ${tw} opacity-60`}>{count}</span>
      <div className="flex-1 h-px bg-slate-800/60" />
    </div>
  );
}

// ── Ready to Schedule Card ────────────────────────────────────────────────────

function ReadyCard({ item, onClick, highlightFirst }) {
  const { referral, workflow } = item;
  const priority  = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const outcome   = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  // Row tint precedence: clinical URGENCY (Gate outcome urgent OR priority urgent)
  // dominates over operational HIGH priority, so an urgent referral never reads as
  // peach/orange. Outcome is the clinical signal; priority is operational.
  const isUrgent  = String(outcome ?? '').toLowerCase() === 'urgent' || priority === WORKFLOW_PRIORITY.URGENT;
  const isHighPri = !isUrgent && priority === WORKFLOW_PRIORITY.HIGH;

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      {...(highlightFirst ? { 'data-learning-target': 'scheduling-first-referral' } : {})}
      className={[
        'w-full text-left flex items-center gap-4 px-5 py-4 rounded-xl border transition-all hover:border-slate-600/60 hover:bg-slate-800/60 group',
        isUrgent  ? 'bg-red-500/5 border-red-500/20'
        : isHighPri ? 'bg-orange-500/5 border-orange-500/20'
        :             'bg-slate-800/30 border-slate-700/40',
        highlightFirst ? 'learning-focus-highlight' : '',
      ].join(' ')}
    >
      <div className="w-9 h-9 rounded-lg bg-slate-800/80 border border-slate-700/40 flex items-center justify-center flex-shrink-0">
        <Calendar className="w-4 h-4 text-indigo-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && (
            <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>
          )}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono break-all">{referral?.id ?? '—'}</span>
          {outcome && <RGStatusBadge type="outcome" status={outcome} />}
          {priority && <RGStatusBadge type="priority" status={priority} />}
        </div>
        <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Received {fmtDate(workflow?.triageCompletedAt ?? workflow?.statusEnteredAt ?? referral?.createdAt)}
          {workflow?.ownerUid && (
            <><span className="text-slate-600 mx-0.5">·</span><User className="w-3 h-3" /> Assigned</>
          )}
        </p>
      </div>
      <span className="text-xs text-indigo-400 font-medium group-hover:text-indigo-300 transition-colors flex-shrink-0">
        Review →
      </span>
    </button>
  );
}

// ── Scheduled Card ────────────────────────────────────────────────────────────

function ScheduledCard({ item, onModify, activeFocusTarget }) {
  const { referral, workflow } = item;
  const priority       = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const scheduledAt    = workflow?.scheduling?.scheduledAt;
  const apptDate       = workflow?.scheduling?.appointmentDate;
  const apptTime       = workflow?.scheduling?.appointmentTime;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const locationId     = workflow?.scheduling?.locationId;

  return (
    <div className="flex items-start gap-4 px-5 py-4 rounded-xl bg-teal-500/5 border border-teal-500/20">
      <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Calendar className="w-4 h-4 text-teal-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && (
            <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>
          )}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono break-all">{referral?.id ?? '—'}</span>
          {priority && <RGStatusBadge type="priority" status={priority} />}
        </div>
        <div className="mt-2 space-y-0.5">
          {apptDate ? (
            <p className="text-xs text-teal-300 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
              })}
              {apptTime && ` · ${from24h(apptTime) ?? apptTime}`}
            </p>
          ) : scheduledAt ? (
            <p className="text-xs text-teal-300 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {fmtTs(scheduledAt)}
            </p>
          ) : null}
          {consultantName && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User className="w-3 h-3" />
              <span className="text-slate-600">Specialist:</span> {consultantName}
            </p>
          )}
          {locationId && (
            <p className="text-xs text-slate-500">{locationId}</p>
          )}
        </div>
      </div>
      <button
        type="button"
        data-learning-target="scheduling-modify-action"
        onClick={() => onModify(item)}
        className={`px-3 py-1.5 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-xs font-medium transition-all flex-shrink-0 mt-0.5${activeFocusTarget === 'scheduling-modify-action' ? ' learning-focus-highlight-subtle' : ''}`}
      >
        Modify
      </button>
    </div>
  );
}

// ── Outcome Card (was ClosedCard) ────────────────────────────────────────────

function OutcomeCard({ item }) {
  const { referral, workflow } = item;
  const priority       = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const rti            = workflow?.returnToIntake ?? {};
  const isReturned     = workflow?.status === WORKFLOW_STATUS.NEEDS_INFO && rti?.returnedFrom === 'SCHEDULING';
  const isCancelled    = workflow?.status === WORKFLOW_STATUS.CANCELLED;
  const isNoShow       = workflow?.status === WORKFLOW_STATUS.NO_SHOW;
  const cancelType     = workflow?.cancelled?.cancelType ?? null;
  const closedAt       = isNoShow
    ? workflow?.noShow?.noShowAt
    : isCancelled ? (workflow?.cancelled?.cancelledAt ?? workflow?.cancelledAt) : workflow?.closedLoop?.closedAt;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const apptDate       = workflow?.scheduling?.appointmentDate ?? workflow?.lastScheduledAppointment?.appointmentDate;
  const apptTime       = workflow?.scheduling?.appointmentTime ?? workflow?.lastScheduledAppointment?.appointmentTime;
  const outcome        = referral?.finalOutcome ?? referral?.outcome?.status ?? null;

  const bgClass   = isReturned ? 'bg-amber-500/5 border-amber-500/20'   : isNoShow ? 'bg-orange-500/5 border-orange-500/20'  : isCancelled ? 'bg-red-500/5 border-red-500/20'     : 'bg-emerald-500/5 border-emerald-500/20';
  const iconBg    = isReturned ? 'bg-amber-500/10 border-amber-500/20'  : isNoShow ? 'bg-orange-500/10 border-orange-500/20' : isCancelled ? 'bg-red-500/10 border-red-500/20'    : 'bg-emerald-500/10 border-emerald-500/20';
  const iconColor = isReturned ? 'text-amber-400'                       : isNoShow ? 'text-orange-400'                       : isCancelled ? 'text-red-400'                        : 'text-emerald-400';
  const tsColor   = isReturned ? 'text-amber-300/70'                    : isNoShow ? 'text-orange-300/70'                    : isCancelled ? 'text-red-300/70'                     : 'text-emerald-300/70';
  const Icon      = isReturned ? Undo2                                  : isNoShow ? Clock                                   : isCancelled ? XCircle                                : CheckCircle2;

  const statusLabel = isReturned
    ? 'Returned to Intake'
    : isNoShow
    ? 'No-Show'
    : isCancelled
      ? (cancelType === 'CLINIC' ? 'Cancelled — Clinic' : 'Cancelled — Patient')
      : 'Closed Loop';

  const badgeLabel = isReturned
    ? 'Returned to Intake'
    : isNoShow
    ? 'No-Show'
    : isCancelled
      ? (cancelType === 'CLINIC' ? 'Cancel — Clinic' : 'Cancel — Patient')
      : 'Closed Loop';

  // Scheduling sub-state → canonical workflow status for badge color (label kept verbatim):
  // Returned → Needs Info (orange), No-Show → rose, Cancelled → red, else Closed Loop (teal).
  const badgeStatus = isReturned
    ? WORKFLOW_STATUS.NEEDS_INFO
    : isNoShow
    ? WORKFLOW_STATUS.NO_SHOW
    : isCancelled
    ? WORKFLOW_STATUS.CANCELLED
    : WORKFLOW_STATUS.CLOSED_LOOP;

  return (
    <div className={`flex items-start gap-4 px-5 py-4 rounded-xl border ${bgClass}`}>
      <div className={`w-9 h-9 rounded-lg border ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && (
            <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>
          )}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono break-all">{referral?.id ?? '—'}</span>
          <RGStatusBadge type="workflow" status={badgeStatus} labelOverride={badgeLabel} />
          {outcome && <RGStatusBadge type="outcome" status={outcome} />}
          {priority !== WORKFLOW_PRIORITY.NORMAL && (
            <RGStatusBadge type="priority" status={priority} />
          )}
        </div>
        <div className="mt-2 space-y-0.5">
          {isReturned && (
            <>
              <p className={`text-xs ${tsColor} flex items-center gap-1`}>
                <Undo2 className="w-3 h-3" />
                Returned to Intake {rti.returnedAt ? fmtTs(rti.returnedAt) : ''}
                {rti.returnedByName ? ` · by ${rti.returnedByName}` : ''}
              </p>
              {rti.reasonCode && (
                <p className="text-xs text-slate-500">
                  Reason: {RETURN_REASON_LABELS[rti.reasonCode] ?? rti.reasonCode}
                </p>
              )}
              {rti.receiverUid && (
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  <span className="text-slate-600">Notified:</span> Intake Coordinator
                </p>
              )}
            </>
          )}
          {closedAt && (
            <p className={`text-xs ${tsColor} flex items-center gap-1`}>
              <Icon className="w-3 h-3" />
              {statusLabel} {fmtTs(closedAt)}
            </p>
          )}
          {isCancelled && workflow?.cancelled?.cancelReason && (
            <p className="text-xs text-slate-500">
              {CANCEL_REASON_LABELS[workflow.cancelled.cancelReason] ?? workflow.cancelled.cancelReason}
            </p>
          )}
          {apptDate && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Appointment{' '}
              {new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
              })}
              {apptTime && ` at ${from24h(apptTime)}`}
            </p>
          )}
          {consultantName && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User className="w-3 h-3" />
              <span className="text-slate-600">Specialist:</span> {consultantName}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Load More Row ─────────────────────────────────────────────────────────────

function LoadMoreRow({ remaining, onLoad }) {
  return (
    <div className="pt-2 pb-1 flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={onLoad}
        className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all"
      >
        Load more
      </button>
      <p className="text-[11px] text-slate-600">{remaining} remaining</p>
    </div>
  );
}

// (SeenCard removed — specialist worklist owns post-consult display)


// ── Tab bar ───────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'ready',     label: 'Ready to Schedule', activeColor: 'text-violet-400 border-violet-500',   learningTarget: 'scheduling-ready-tab' },
  { id: 'scheduled', label: 'Scheduled',          activeColor: 'text-teal-400 border-teal-500',       learningTarget: 'scheduling-scheduled-tab' },
  { id: 'closed',    label: 'Referral Outcomes',   activeColor: 'text-emerald-400 border-emerald-500', learningTarget: 'scheduling-outcomes-tab' },
];

function TabBar({ active, counts, onChange, activeFocusTarget }) {
  return (
    <div className="flex gap-1 border-b border-slate-800/60 mb-6">
      {TABS.map(({ id, label, activeColor, learningTarget }) => {
        const isActive = active === id;
        const count    = counts[id] ?? 0;
        const isHighlighted = activeFocusTarget === learningTarget;
        return (
          <button
            key={id}
            type="button"
            data-learning-target={learningTarget}
            onClick={() => onChange(id)}
            className={[
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px',
              isActive
                ? `${activeColor} bg-transparent`
                : 'text-slate-500 border-transparent hover:text-slate-300',
              isHighlighted ? 'learning-focus-highlight' : '',
            ].join(' ')}
          >
            {label}
            <span className={[
              'text-xs px-1.5 py-0.5 rounded-full font-semibold',
              isActive ? 'bg-slate-700/80' : 'bg-slate-800/60 text-slate-600',
            ].join(' ')}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function SchedulingView({ activeFocusTarget, onLearningContext, autoOpenReferralId }) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const uid      = user?.uid;
  const role     = user?.role ?? 'scheduler';

  const PAGE_SIZE = 25;

  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState(null);
  const [activeTab,   setActiveTab]   = useState('ready');
  const [bookingItem, setBookingItem] = useState(null);
  const [modifyItem,  setModifyItem]  = useState(null);
  const [drawerItem,  setDrawerItem]  = useState(null);

  // ── Referral Outcomes tab state ─────────────────────────────────────────────
  const [closedItems,        setClosedItems]        = useState([]);
  const [closedLoaded,       setClosedLoaded]       = useState(false);
  const [closedLoading,      setClosedLoading]      = useState(false);
  const [closedError,        setClosedError]        = useState(null);
  const [closedVisibleCount, setClosedVisibleCount] = useState(PAGE_SIZE);
  const [outcomeFilter,      setOutcomeFilter]      = useState('ALL');

  useEffect(() => {
    if (onLearningContext) {
      onLearningContext({
        schedulingDrawerOpen: !!drawerItem,
        schedulingTabScheduled: activeTab === 'scheduled',
      });
    }
    return () => { if (onLearningContext) onLearningContext({}); };
  }, [drawerItem, activeTab, onLearningContext]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 100 });
      setItems(data.filter(({ workflow }) =>
        workflow?.status === WORKFLOW_STATUS.READY_TO_SCHEDULE ||
        workflow?.status === WORKFLOW_STATUS.SCHEDULED
      ));
    } catch (err) {
      setLoadError(safeUserError(err) ?? 'Failed to load scheduling queue. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: open the exact referral when arriving from "Open Referral" (Referral
  // Pings). Fires once after the active queue loads; activates the matching tab and
  // opens the WorkItemDrawer. Shows a notice if the referral isn't in this workspace.
  const [deepLinkNotice, setDeepLinkNotice] = useState('');
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading) return;
    autoOpenFired.current = true;
    const res = findReferralInBuckets([{ tab: 'scheduling', items }], autoOpenReferralId);
    if (res.found) {
      setActiveTab(res.item.workflow?.status === WORKFLOW_STATUS.SCHEDULED ? 'scheduled' : 'ready');
      setDrawerItem(res.item);
    } else {
      // Not in a scheduling stage / not loaded here — read-only fallback (non-PHI
      // core) for a specific reason instead of a vague "not available".
      getReferral(tenantId, autoOpenReferralId)
        .then((ref) => setDeepLinkNotice(classifyDeepLinkFailure({ referral: ref })))
        .catch((err) => setDeepLinkNotice(classifyDeepLinkFailure({ error: err })));
    }
  }, [autoOpenReferralId, loading, items, tenantId]);

  const loadClosed = useCallback(async () => {
    if (!tenantId) return;
    setClosedLoading(true);
    setClosedError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 200 });
      setClosedItems(sortClosed(data.filter(({ workflow }) =>
        workflow?.status === WORKFLOW_STATUS.CLOSED_LOOP ||
        workflow?.status === WORKFLOW_STATUS.CANCELLED ||
        workflow?.status === WORKFLOW_STATUS.NO_SHOW ||
        // After-scheduling visibility: referrals returned from Scheduling to Intake
        // (now NEEDS_INFO). Tenant-wide, read-only.
        (workflow?.status === WORKFLOW_STATUS.NEEDS_INFO &&
         workflow?.returnToIntake?.returnedFrom === 'SCHEDULING')
      )));
      setClosedLoaded(true);
    } catch (err) {
      setClosedError(safeUserError(err) ?? 'Failed to load referral outcomes. Please retry.');
    } finally {
      setClosedLoading(false);
    }
  }, [tenantId]);

  // Lazy load on first open; reset page on tab switch.
  useEffect(() => { if (activeTab === 'closed' && !closedLoaded) loadClosed(); }, [activeTab, closedLoaded, loadClosed]);
  useEffect(() => { setClosedVisibleCount(PAGE_SIZE); setOutcomeFilter('ALL'); }, [activeTab]);

  const handleBooked = useCallback((referralId, details) => {
    setItems((prev) => prev.map((item) => {
      if (item.referral?.id !== referralId) return item;
      return {
        ...item,
        workflow: {
          ...item.workflow,
          status: WORKFLOW_STATUS.SCHEDULED,
          scheduling: {
            ...(item.workflow?.scheduling ?? {}),
            scheduledAt:     { seconds: Math.floor(Date.now() / 1000) },
            scheduledByUid:  uid,
            scheduledBy:     uid,
            appointmentDate: details.appointmentDate,
            appointmentTime: details.appointmentTime,
            consultantId:    details.consultantId,
            consultantName:  details.consultantName,
            locationId:      details.locationId,
            schedulingNote:  details.schedulingNote,
          },
        },
      };
    }));
    setActiveTab('scheduled');
  }, [uid]);

  const handleReturned = useCallback((referralId) => {
    setItems((prev) => prev.filter((item) => item.referral?.id !== referralId));
    setDrawerItem(null);
  }, []);

  const handleRescheduled = useCallback((referralId, newAppointment) => {
    setItems((prev) => prev.map((item) => {
      if (item.referral?.id !== referralId) return item;
      return {
        ...item,
        workflow: {
          ...item.workflow,
          scheduling: {
            ...(item.workflow?.scheduling ?? {}),
            appointmentDate: newAppointment.appointmentDate,
            appointmentTime: newAppointment.appointmentTime,
            consultantId:    newAppointment.consultantId,
            consultantName:  newAppointment.consultantName,
            locationId:      newAppointment.locationId,
            schedulingNote:  newAppointment.schedulingNote,
          },
        },
      };
    }));
    setModifyItem(null);
  }, []);

  const handleCancelled = useCallback((referralId, newStatus) => {
    setItems((prev) => {
      const target = prev.find((it) => it.referral?.id === referralId);
      const remaining = prev.filter((it) => it.referral?.id !== referralId);

      if (newStatus === WORKFLOW_STATUS.CANCELLED && target) {
        setClosedItems((closed) => sortClosed([
          ...closed,
          {
            ...target,
            workflow: {
              ...target.workflow,
              status: WORKFLOW_STATUS.CANCELLED,
              cancelledAt: { seconds: Math.floor(Date.now() / 1000) },
            },
          },
        ]));
      } else if (newStatus === WORKFLOW_STATUS.NO_SHOW && target) {
        setClosedItems((closed) => sortClosed([
          ...closed,
          {
            ...target,
            workflow: {
              ...target.workflow,
              status: WORKFLOW_STATUS.NO_SHOW,
              noShow: {
                noShowAt: { seconds: Math.floor(Date.now() / 1000) },
                noShowMarkedBy: target.workflow?.updatedByUid ?? null,
              },
              lastScheduledAppointment: target.workflow?.scheduling
                ? { ...target.workflow.scheduling }
                : null,
            },
          },
        ]));
      } else if (newStatus === WORKFLOW_STATUS.READY_TO_SCHEDULE && target) {
        remaining.push({
          ...target,
          workflow: {
            ...target.workflow,
            status: WORKFLOW_STATUS.READY_TO_SCHEDULE,
            scheduling: {},
          },
        });
      }

      return remaining;
    });
    setModifyItem(null);
  }, []);

  const ready     = sortReady(items.filter((i) => {
    if (i.workflow?.status !== WORKFLOW_STATUS.READY_TO_SCHEDULE) return false;
    if (i.workflow?.onHold === true) return false;
    if (!i.workflow?.triage?.triageCompletedAt && !i.workflow?.triageCompletedAt) return false;
    return true;
  }));
  const scheduled = sortScheduled(items.filter((i) => i.workflow?.status === WORKFLOW_STATUS.SCHEDULED));

  const matchesOutcome = (item, filterId) => {
    const st = item.workflow?.status;
    if (filterId === 'ALL')            return true;
    if (filterId === 'CLOSED_LOOP')    return st === WORKFLOW_STATUS.CLOSED_LOOP;
    if (filterId === 'NO_SHOW')        return st === WORKFLOW_STATUS.NO_SHOW;
    if (filterId === 'CANCEL_PATIENT') return st === WORKFLOW_STATUS.CANCELLED;
    if (filterId === 'RETURNED_TO_INTAKE') {
      return st === WORKFLOW_STATUS.NEEDS_INFO && item.workflow?.returnToIntake?.returnedFrom === 'SCHEDULING';
    }
    return true;
  };

  const OUTCOME_CHIPS = [
    { id: 'ALL',                 label: 'All',                count: closedItems.length },
    { id: 'RETURNED_TO_INTAKE',  label: 'Returned to Intake', count: closedItems.filter((i) => matchesOutcome(i, 'RETURNED_TO_INTAKE')).length },
    { id: 'CLOSED_LOOP',         label: 'Closed Loop',        count: closedItems.filter((i) => matchesOutcome(i, 'CLOSED_LOOP')).length },
    { id: 'NO_SHOW',             label: 'No-Show',            count: closedItems.filter((i) => matchesOutcome(i, 'NO_SHOW')).length },
    { id: 'CANCEL_PATIENT',      label: 'Cancel — Patient',    count: closedItems.filter((i) => matchesOutcome(i, 'CANCEL_PATIENT')).length },
  ];

  const filteredOutcomes = closedItems.filter((item) => matchesOutcome(item, outcomeFilter));

  const tabCounts = { ready: ready.length, scheduled: scheduled.length, closed: closedItems.length };

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Reset page on tab switch so the new tab always starts at the top.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeTab]);

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Scheduling Queue</h1>
            <p className="text-xs text-slate-500 mt-0.5">Book appointments for specialist review.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white text-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Loading scheduling queue…</p>
          </div>
        </div>
      )}

      {/* Load error */}
      {!loading && loadError && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-300 font-medium">Failed to load scheduling queue</p>
            <p className="text-xs text-red-400/80 mt-0.5">{loadError}</p>
            <button onClick={load} className="mt-2 text-xs text-red-300 underline hover:no-underline">
              Retry
            </button>
          </div>
        </div>
      )}

      {deepLinkNotice && (
        <div className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      {/* Tabs + Content */}
      {!loading && !loadError && (
        <>
          <TabBar active={activeTab} counts={tabCounts} onChange={setActiveTab} activeFocusTarget={activeFocusTarget} />

          {/* Ready to Schedule */}
          {activeTab === 'ready' && (
            <div className="space-y-2" data-learning-target="scheduling-book-action">
              {ready.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No referrals waiting to be booked</p>
                </div>
              ) : (
                <>
                  {ready.slice(0, visibleCount).map((item, idx) => (
                    <ReadyCard
                      key={item.referral.id}
                      item={item}
                      onClick={setDrawerItem}
                      highlightFirst={idx === 0 && activeFocusTarget === 'scheduling-first-referral'}
                    />
                  ))}
                  {ready.length > visibleCount && (
                    <LoadMoreRow remaining={ready.length - visibleCount} onLoad={() => setVisibleCount((c) => c + PAGE_SIZE)} />
                  )}
                </>
              )}
            </div>
          )}

          {/* Scheduled — Upcoming */}
          {activeTab === 'scheduled' && (
            <div className="space-y-2">
              {scheduled.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No booked appointments yet</p>
                </div>
              ) : (
                <>
                  {scheduled.slice(0, visibleCount).map((item) => (
                    <ScheduledCard key={item.referral.id} item={item} onModify={setModifyItem} activeFocusTarget={activeFocusTarget} />
                  ))}
                  {scheduled.length > visibleCount && (
                    <LoadMoreRow remaining={scheduled.length - visibleCount} onLoad={() => setVisibleCount((c) => c + PAGE_SIZE)} />
                  )}
                </>
              )}
            </div>
          )}

          {/* Referral Outcomes */}
          {activeTab === 'closed' && (
            <>
              {/* Outcome filter chips */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {OUTCOME_CHIPS.map(({ id, label, count }) => {
                  const isActive = outcomeFilter === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { setOutcomeFilter(id); setClosedVisibleCount(PAGE_SIZE); }}
                      className={[
                        'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                        isActive
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                          : 'bg-slate-800/50 text-slate-400 border-slate-700/40 hover:text-slate-200 hover:border-slate-600/60',
                      ].join(' ')}
                    >
                      {label}
                      <span className={[
                        'ml-1.5 text-[10px] px-1 py-0.5 rounded-full',
                        isActive ? 'bg-emerald-500/30' : 'bg-slate-700/60',
                      ].join(' ')}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {closedLoading && (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                </div>
              )}
              {!closedLoading && closedError && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-300 font-medium">Failed to load referral outcomes</p>
                    <p className="text-xs text-red-400/80 mt-0.5">{closedError}</p>
                    <button onClick={loadClosed} className="mt-2 text-xs text-red-300 underline hover:no-underline">Retry</button>
                  </div>
                </div>
              )}
              {!closedLoading && !closedError && filteredOutcomes.length === 0 && (
                <div className="text-center py-16">
                  <CheckCircle2 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">
                    {outcomeFilter === 'ALL' ? 'No referral outcomes yet' : `No ${OUTCOME_CHIPS.find((c) => c.id === outcomeFilter)?.label ?? ''} outcomes`}
                  </p>
                  <p className="text-slate-600 text-sm mt-1">Completed, cancelled, and no-show referrals will appear here.</p>
                </div>
              )}
              {!closedLoading && !closedError && filteredOutcomes.length > 0 && (
                <div className="space-y-2">
                  {filteredOutcomes.slice(0, closedVisibleCount).map((item) => (
                    <OutcomeCard key={item.referral.id} item={item} />
                  ))}
                  {filteredOutcomes.length > closedVisibleCount && (
                    <LoadMoreRow
                      remaining={filteredOutcomes.length - closedVisibleCount}
                      onLoad={() => setClosedVisibleCount((c) => c + PAGE_SIZE)}
                    />
                  )}
                </div>
              )}
            </>
          )}

        </>
      )}

      {/* Work Item Drawer (full detail view, same as Intake Coordinator) */}
      {drawerItem && !bookingItem && (
        <WorkItemDrawer
          item={drawerItem}
          onClose={() => setDrawerItem(null)}
          activeFocusTarget={activeFocusTarget}
          onWorkflowUpdated={(referralId, newWorkflow) => {
            // If returned to intake (NEEDS_INFO), remove from scheduler list
            if (newWorkflow?.status === WORKFLOW_STATUS.NEEDS_INFO) {
              handleReturned(referralId);
              return;
            }
            // Otherwise update the item in-place (e.g. scheduled, seen)
            setItems((prev) => prev.map((it) =>
              it.referral?.id === referralId ? { ...it, workflow: newWorkflow } : it
            ));
            setDrawerItem(null);
          }}
        />
      )}

      {bookingItem && (
        <BookAppointmentModal
          item={bookingItem}
          uid={uid}
          role={role}
          onClose={() => setBookingItem(null)}
          onBooked={handleBooked}
          scheduledItems={scheduled}
        />
      )}

      {modifyItem && (
        <ModifyScheduledModal
          item={modifyItem}
          uid={uid}
          role={role}
          onClose={() => setModifyItem(null)}
          onRescheduled={handleRescheduled}
          onCancelled={handleCancelled}
          scheduledItems={scheduled}
        />
      )}
    </div>
  );
}
